import apiService from '../Api'
import {
  BlogPost,
  BlogPostComplete,
  BlogPostDraft,
  BlogPostDraftFormData,
  KnowledgeArea,
  PopularBlogPost
} from './types'

export interface IBlogPostFields {
  title: string
  header: string
  body: string
  image?: File | null
  image_alt_text: string
  is_active: boolean
  scheduled_at: string | null
  knowledge_areas: number[]
  seo_title: string
  meta_description: string
  seo_keywords: string[]
  seo_slug: string
  schema_type: 'Article' | 'BlogPosting' | 'NewsArticle'
  schema_headline: string
  schema_description: string
}

export class BlogPostService {
  async list(queryParams: { search: string; page_size: number; tag: string; status: string; page: number }): Promise<{
    data: BlogPost[]
    current_page: number
    per_page: number
    total: number
    total_filtered: number
  }> {
    const params = new URLSearchParams()
    params.append('search', queryParams.search)
    params.append('page_size', queryParams.page_size.toString())
    params.append('tag', queryParams.tag)
    params.append('status', queryParams.status)
    params.append('page', queryParams.page.toString())

    return apiService.get('/blog-posts', { params }).then((response) => response.data)
  }

  async get(id: number): Promise<BlogPostComplete> {
    return apiService.get(`/blog-posts/${id}`).then((response) => {
      const data = response.data
      if (typeof data.seo_keywords === 'string') {
        try {
          data.seo_keywords = JSON.parse(data.seo_keywords)
        } catch {
          data.seo_keywords = []
        }
      }
      return data
    })
  }

  async delete(id: number): Promise<void> {
    return apiService.delete(`/blog-posts/${id}`)
  }

  async getKnowledgeAreas(): Promise<KnowledgeArea[]> {
    return apiService.get('/knowledge-areas').then((response) => response.data.areas)
  }

  async create(form: IBlogPostFields): Promise<BlogPostComplete> {
    const formData = new FormData()
    formData.append('title', form.title)
    formData.append('header', form.header)
    formData.append('body', form.body)
    formData.append('image_alt_text', form.image_alt_text)
    formData.append('is_active', form.is_active ? '1' : '0')
    if (form.scheduled_at) {
      formData.append('scheduled_at', form.scheduled_at)
    }
    formData.append('seo_title', form.seo_title)
    formData.append('meta_description', form.meta_description)
    form.seo_keywords.forEach((keyword) => {
      formData.append('seo_keywords[]', keyword)
    })
    formData.append('seo_slug', form.seo_slug)

    if (form.image) {
      formData.append('image', form.image)
    }

    form.knowledge_areas.forEach((areaId) => {
      formData.append('knowledge_areas[]', areaId.toString())
    })

    formData.append('schema_type', form.schema_type)
    formData.append('schema_headline', form.schema_headline)
    formData.append('schema_description', form.schema_description)

    return apiService.post('/blog-posts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async update(id: number, form: IBlogPostFields): Promise<BlogPostComplete> {
    const formData = new FormData()
    formData.append('_method', 'PUT')
    formData.append('title', form.title)
    formData.append('header', form.header)
    formData.append('body', form.body)
    formData.append('image_alt_text', form.image_alt_text)
    formData.append('is_active', form.is_active ? '1' : '0')
    if (form.scheduled_at) {
      formData.append('scheduled_at', form.scheduled_at)
    } else {
      formData.append('scheduled_at', '')
    }
    formData.append('seo_title', form.seo_title)
    formData.append('meta_description', form.meta_description)
    formData.append('seo_slug', form.seo_slug)

    if (form.image) {
      formData.append('image', form.image)
    }

    form.seo_keywords.forEach((keyword) => {
      formData.append('seo_keywords[]', keyword)
    })

    form.knowledge_areas.forEach((areaId) => {
      formData.append('knowledge_areas[]', areaId.toString())
    })

    formData.append('schema_type', form.schema_type)
    formData.append('schema_headline', form.schema_headline)
    formData.append('schema_description', form.schema_description)

    return apiService.post(`/blog-posts/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async listPopulars(): Promise<PopularBlogPost[]> {
    return apiService.get('/blog-posts/populars').then((response) => response.data)
  }

  async setPopular(popularIndex: number, id: number): Promise<BlogPostComplete> {
    return apiService.put(`/blog-posts/set-popular/${id}`, { popularIndex })
  }

  async toggleActive(id: number): Promise<{ message: string; is_active: boolean }> {
    return apiService.patch(`/blog-posts/${id}/toggle-active`).then((response) => response.data)
  }

  async listDrafts(): Promise<BlogPostDraft[]> {
    return apiService.get('/blog-post-drafts').then((response) => response.data)
  }

  async createDraft(data: {
    title: string | null
    body: string | null
    form_data: BlogPostDraftFormData
  }): Promise<BlogPostDraft> {
    return apiService.post('/blog-post-drafts', data).then((response) => response.data)
  }

  async getDraft(id: number): Promise<BlogPostDraft> {
    return apiService.get(`/blog-post-drafts/${id}`).then((response) => response.data)
  }

  async deleteDraft(id: number): Promise<void> {
    return apiService.delete(`/blog-post-drafts/${id}`)
  }

  async uploadDraftImage(file: File): Promise<{ path: string; url: string }> {
    const formData = new FormData()
    formData.append('image', file)
    return apiService
      .post('/blog-post-drafts/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => response.data)
  }

  async getDraftImageBlob(id: number): Promise<Blob> {
    return apiService.get(`/blog-post-drafts/${id}/image`, { responseType: 'blob' }).then((response) => response.data)
  }
}
